import { connect } from 'react-redux';
import { FormSpy } from 'react-final-form';
import { isAfter, isBefore, isValid, max, min } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';

import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { KnmAttachmentsSigning } from 'KnmPackage/MainModule/components/KnmAttachmentsSigning';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { KnmAddressField } from 'KnmPackage/MainModule/components/KnmAddressField';
import { Button } from 'UIPackage/MainModule/components/Button';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { formatDate, parseDate } from 'CorePackage/MainModule/common/formatDate';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { getKnmStartDate, getKnmStopDate } from 'KnmErknmPackage/MainModule/selectors/knmErknmSelectors';

import { KnmSystemTypesEnum } from '../../KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import {
  getKnmDataCreated,
  getKnmDataSigned,
  getKnmFormRef,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmOnSaveButtonClick,
  getKnmSystemType,
} from '../../KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmFormRef } from '../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IRootState } from '../../CorePackage/MainModule/interfaces/IRootState';
import { IKnmErknmFormSuspensionEvent } from '../MainModule/interfaces/IKnmErknmFormSuspensionEvent';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
} from '../../CorePackage/MainModule/constants/dateFormats';
import { IKnmErknmFormEventSelectOption } from '../ErknmInfoModule/interfaces/IKnmErknmFormEventSelectOption';
import styles from './ErknmSuspensionEvents.module.scss';
import { IKnmForm } from '../../KnmPackage/MainModule/interfaces/IKnmForm';
import { CheckboxField } from '../../UIPackage/MainModule/components/CheckboxField';
import { checkHasErrorInCollapseBlock } from '../../functions/checkHasErrorInCollapseBlock';

interface IErknmSuspensionEventItemProps {
  name: string;
  parentFieldName: string;
  values: IKnmErknmFormSuspensionEvent;
  index: number;
  isKindDP: boolean;
  isEnable: boolean;
  eventOptions: IKnmErknmFormEventSelectOption[];
  remove: (index: number) => void;
  isValid412: boolean;
}

interface IErknmSuspensionEventItemStoreProps {
  formRef: IKnmFormRef | undefined;
  knmSystemType?: KnmSystemTypesEnum;
  signed: boolean | null | undefined;
  onSaveButtonClick: (() => void) | undefined;
  created: string | null | undefined;
  settings: IAdminSetting[] | undefined;
  startDateKnm: string | undefined;
  stopDateKnm: string | undefined;
  isSupervisor: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isKnmStatusTypeCarriedOut: boolean;
}

const ErknmSuspensionEventItemWithoutStore = ({
  formRef,
  name,
  parentFieldName,
  values,
  index,
  isKindDP,
  isEnable,
  eventOptions,
  remove,
  knmSystemType,
  onSaveButtonClick,
  signed,
  isValid412,
  created,
  settings,
  startDateKnm,
  stopDateKnm,
  isSupervisor,
  isKnmStatusTypeWaitingCarryOut,
  isKnmStatusTypeCarriedOut,
}: IErknmSuspensionEventItemProps & IErknmSuspensionEventItemStoreProps) => {
  const [isKnmCollapseOpened, setIsKnmCollapseOpened] = useState<boolean>(true);
  const [isDisabledBtnEndAfterClick, setIsDisabledBtnEndAfterClick] = useState(false);
  const { guid, attachments, startDate, stopDate, isInitStopDateFilled, event, decisionDateTime } = values;
  const isShowConsiderWorkingDays = startDate && stopDate;
  const collapseTitle = isKindDP ? `Мотивированное представление № ${index + 1}` : `Решение о приостановлении`;
  // : `Решение о приостановлении № ${guid ? decisionNumber : ''}`;
  const isWatchOnly = !isEnable || !!guid;
  const isStopDateWatchOnly = !isEnable || isInitStopDateFilled;
  const considerWorkingDaysFieldName = `${name}.isWorkDaysOnly` as keyof IKnmForm;
  const stopDateFieldName = `${name}.stopDate` as keyof IKnmForm;
  const completedFieldName = `${name}.completed` as keyof IKnmForm;

  useEffect(() => {
    if (!isShowConsiderWorkingDays) formRef?.current.change(considerWorkingDaysFieldName);
  }, [isShowConsiderWorkingDays, formRef, name, considerWorkingDaysFieldName]);

  const [isVisibleSignModal, setIsVisibleSignModal] = useState<boolean>(false);

  const handleOpenSignModal = useCallback(() => {
    setIsVisibleSignModal(true);
  }, []);

  const handleCloseSignModal = useCallback(() => {
    setIsVisibleSignModal(false);
  }, []);

  const handleEndSuspensionEvent = useCallback(() => {
    setIsDisabledBtnEndAfterClick(true);
    let targetValue = '';
    const parsedStartDate = parseDate({ date: startDate || '', parseFormat: DATE_FORMAT_LONG_FNS });

    if (startDate && parsedStartDate && isAfter(parsedStartDate, new Date())) {
      targetValue = startDate;
    } else {
      targetValue = formatDate({
        date: new Date(),
        dateFormat: DATE_FORMAT_LONG_FNS,
        parseFormat: DATE_FORMAT_ISO_FNS,
      });
    }

    formRef?.current.change(stopDateFieldName, targetValue);
    formRef?.current.change(completedFieldName, true);
    if (onSaveButtonClick) {
      onSaveButtonClick();
    }
  }, [formRef, onSaveButtonClick, startDate]);

  const parsedStopDate = parseDate({ date: stopDate || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const parsedStartDate = parseDate({ date: startDate || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const isEndSuspensionEventBtnDisabled =
    event?.type?.title !== 'Получение письменных объяснений' ||
    isDisabledBtnEndAfterClick ||
    values.isNew ||
    !signed ||
    isAfter(new Date(), parsedStopDate || new Date()) ||
    isBefore(new Date(), parsedStartDate || new Date());
  const canEndSuspensionEvent = isSupervisor && (isKnmStatusTypeWaitingCarryOut || isKnmStatusTypeCarriedOut);

  const isEnablePaperlessDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  const hasError = checkHasErrorInCollapseBlock(formRef, parentFieldName);

  const parsedStartDateKnm = parseDate({ date: startDateKnm || '', parseFormat: DATE_FORMAT_LONG_FNS });

  const parseStartDateSuspension = parseDate({
    date: decisionDateTime || '',
    parseFormat: DATE_FORMAT_LONG_MINUTE_FNS,
  });

  const path = `${name}.startDate` as keyof IKnmForm;
  const startDateEventField = (formRef as IKnmFormRef)?.current.getFieldState(path)?.value;
  const baseStartDate = parseStartDateSuspension || parsedStartDateKnm;

  /**
   * Ограничение max date поля "Дата начала приостановления" минимальной из дат
   * окончания всего КНМ и окончания текущего действия
   */
  const matchedEvent = eventOptions?.find(option => option.value === event?.value);
  const parsedStopDateKnm = parseDate({ date: stopDateKnm || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const parsedEventStopDate = parseDate({ date: matchedEvent?.stopDate || '', parseFormat: DATE_FORMAT_LONG_FNS });
  const startDateMaxDates = [parsedStopDateKnm, parsedEventStopDate].filter((date): date is Date => isValid(date));
  const startDateMaxDate = startDateMaxDates.length ? min(startDateMaxDates) : undefined;

  let baseStopDate = baseStartDate;

  if (startDateEventField) {
    const parseEventStartDateString = parseDate({
      date: startDateEventField?.toString(),
      parseFormat: DATE_FORMAT_LONG_FNS,
    });

    baseStopDate = max(
      [parseStartDateSuspension, parseEventStartDateString].filter((date): date is Date => isValid(date)),
    );
  }

  return (
    <KnmCollapse
      id={name}
      title={collapseTitle}
      onClose={!guid ? () => remove(index) : undefined}
      isNew={!guid}
      error={isKnmCollapseOpened && hasError}
      changeOpenedCallback={setIsKnmCollapseOpened}
    >
      <div>
        <Row>
          <FieldBlock>
            <span>{isKindDP ? 'Мотивированное представление' : 'Файл решения'}</span>
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              onClick={handleOpenSignModal}
              disabled={isWatchOnly || !!attachments?.length}
            />
          </FieldBlock>
          <FormSpy subscription={{ errors: true, touched: true }}>
            {props => {
              const fieldError = props.errors?.[parentFieldName]?.[index]?.attachments;
              const isFieldTouched = props.touched?.[`${name}.attachments`];

              if (fieldError && isFieldTouched) {
                return <p className="shared-input-error shared-row__small-margin">{fieldError}</p>;
              }

              return null;
            }}
          </FormSpy>
          <KnmAttachmentsSigning
            name={`${name}.attachments`}
            isVisibleSignModal={isVisibleSignModal}
            closeSignModal={handleCloseSignModal}
            acceptedFiles=".pdf,.png,.jpeg,.ktd,.rtf,.rar,.zip"
            isWatchOnly={isWatchOnly}
            formRef={formRef}
          />
        </Row>
        <Row>
          <FieldBlock label="Действие" placementLabel="left" labelSize="col-4" color="black">
            <SelectInputField
              className={styles.SelectInput}
              name={`${name}.event`}
              placeholder="Выберите действие"
              size="medium"
              options={eventOptions}
              disabled={isWatchOnly}
            />
          </FieldBlock>
        </Row>
        {!isKindDP && (
          <>
            <Row>
              <FieldBlock label="Дата и время издания решения" placementLabel="left" size="half" color="black">
                <DatePickerField
                  name={`${name}.decisionDateTime`}
                  placeholder="Выберите дату и время"
                  timeFormat
                  disabled={isWatchOnly}
                  minDate={parsedStartDateKnm}
                />
              </FieldBlock>
              {!isEnablePaperlessDocument && (
                <FieldBlock label="Номер решения" placementLabel="left" size="half" color="black">
                  <TextInputField name={`${name}.decisionNumber`} placeholder="Введите номер" disabled={isWatchOnly} />
                </FieldBlock>
              )}
            </Row>
            <Row>
              <FieldBlock label="Место вынесения решения о приостановлении срока КНМ" color="black">
                <KnmAddressField
                  id={`${name}.decisionPlace`}
                  addressFieldPathWithPrefix={`${name}.decisionPlace`}
                  resize="none"
                  placeholder="Введите место"
                  disabled={isWatchOnly}
                  formRef={formRef}
                  prefixNameNotInFias={`${name}.`}
                  knmSystemType={knmSystemType}
                  manualAddressPlaceholder={'Место'}
                />
              </FieldBlock>
            </Row>
          </>
        )}
        <Row>
          <FieldBlock label="Дата начала приостановления срока КНМ" size="half" color="black">
            <DatePickerField
              name={`${name}.startDate`}
              placeholder="Выберите дату"
              disabled={isWatchOnly}
              minDate={baseStartDate}
              maxDate={startDateMaxDate}
            />
          </FieldBlock>
          <FieldBlock label="Дата окончания приостановления срока КНМ" size="half" color="black">
            <DatePickerField
              name={`${name}.stopDate`}
              placeholder="Выберите дату"
              disabled={isStopDateWatchOnly}
              minDate={baseStopDate}
            />
          </FieldBlock>
        </Row>
        <Row justify={isShowConsiderWorkingDays ? undefined : 'end'}>
          {isShowConsiderWorkingDays && (
            <CheckboxField
              labelClassName="shared-field-label_user-select"
              name={considerWorkingDaysFieldName}
              id={considerWorkingDaysFieldName}
              label="При перерасчёте даты окончания КНМ и действия учитывать только рабочие дни"
              size="medium"
              type="checkbox"
              theme="system"
              disabled={isStopDateWatchOnly}
            />
          )}
          {isValid412 && isKindDP && canEndSuspensionEvent && (
            <Button
              className="shared-btn-add"
              text="Завершить приостановление"
              type="button"
              variant="link"
              color="primary"
              onClick={handleEndSuspensionEvent}
              disabled={isEndSuspensionEventBtnDisabled}
            />
          )}
        </Row>
      </div>
    </KnmCollapse>
  );
};

const mapStateToProps = (state: IRootState): IErknmSuspensionEventItemStoreProps => ({
  formRef: getKnmFormRef(state),
  knmSystemType: getKnmSystemType(state),
  signed: getKnmDataSigned(state),
  onSaveButtonClick: getKnmOnSaveButtonClick(state),
  created: getKnmDataCreated(state),
  settings: getAdminFetchSettings(state),
  startDateKnm: getKnmStartDate(state),
  stopDateKnm: getKnmStopDate(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
});

export const ErknmSuspensionEventItem = connect(mapStateToProps)(ErknmSuspensionEventItemWithoutStore);
